import { useRef, useState } from "react";
import { property } from "../data/property";
import { trackEvent } from "../lib/analytics";
import Reveal from "./Reveal";
import { Send, Check, AlertCircle, MessageSquare } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeadForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [subject, setSubject] = useState("Stel een vraag");
  const [company, setCompany] = useState(""); // honeypot
  const [state, setState] = useState("idle"); // idle | loading | success | error
  const [error, setError] = useState("");
  const startedRef = useRef(false);

  const onFocusAny = () => {
    if (!startedRef.current) {
      startedRef.current = true;
      trackEvent("lead_form_start", { subject });
    }
  };

  const changeSubject = (s) => {
    setSubject(s);
    trackEvent("lead_subject_change", { subject: s });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (state === "loading") return;
    setState("loading");
    setError("");

    try {
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          phone: phone || null,
          message,
          subject,
          source: "kempenaar29.nl",
          company,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Er ging iets mis. Probeer het later opnieuw.");
      }
      trackEvent("lead_submit", { subject });
      trackEvent("lead_submit_success", { subject });
      setState("success");
      setName("");
      setEmail("");
      setPhone("");
      setMessage("");
    } catch (err) {
      console.error(err);
      trackEvent("lead_submit_error", { subject, message: String(err.message || "") });
      setError(err.message || "Onbekende fout");
      setState("error");
    }
  };

  return (
    <section
      id="vraag"
      data-testid="lead-form-section"
      className="bg-brand-bg py-24 md:py-32"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 lg:px-20">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-16">
          <Reveal className="md:col-span-5">
            <p className="overline">Direct contact</p>
            <h2 className="mt-6 font-serif text-4xl leading-[1.05] tracking-tight text-brand-ink sm:text-5xl lg:text-6xl text-balance">
              Stel een <span className="italic">vraag</span> of vraag de brochure aan.
            </h2>
            <p className="mt-6 max-w-xl text-lg text-brand-muted">
              Liever niet bellen of niet WhatsAppen? Stuur een berichtje — Carole reageert doorgaans binnen 24 uur, ook 's avonds en in het weekend.
            </p>

            <div className="mt-10 space-y-4 text-sm text-brand-muted">
              <p className="flex items-center gap-2">
                <MessageSquare size={14} className="text-brand-moss" />
                <span>Wij gebruiken je gegevens uitsluitend voor het beantwoorden van je vraag.</span>
              </p>
              <p className="text-xs">
                Liever direct bellen? <a href={`tel:${property.fundaUrl ? "+31622333667" : ""}`} className="text-brand-moss underline">06 22 33 36 67</a>
              </p>
            </div>
          </Reveal>

          <Reveal className="md:col-span-7" delay={0.12}>
            <form
              data-testid="lead-form"
              onSubmit={submit}
              onFocus={onFocusAny}
              className="border border-brand-border bg-brand-surface p-8 md:p-10"
              noValidate
            >
              {state === "success" ? (
                <SuccessState onReset={() => setState("idle")} />
              ) : (
                <>
                  {/* Subject pills */}
                  <fieldset className="mb-8">
                    <legend className="overline">Onderwerp</legend>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {["Stel een vraag", "Aanvraag brochure", "Plan bezichtiging"].map((s) => (
                        <button
                          type="button"
                          key={s}
                          data-testid={`subject-${s.replace(/\s+/g, "-").toLowerCase()}`}
                          onClick={() => changeSubject(s)}
                          className={`border px-4 py-2 text-sm transition-colors ${
                            subject === s
                              ? "border-brand-moss bg-brand-moss text-white"
                              : "border-brand-border text-brand-ink hover:border-brand-moss"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </fieldset>

                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                    <Field
                      label="Naam *"
                      type="text"
                      autoComplete="name"
                      value={name}
                      onChange={setName}
                      required
                      testId="lead-name"
                    />
                    <Field
                      label="E-mail *"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={setEmail}
                      required
                      testId="lead-email"
                    />
                  </div>
                  <Field
                    label="Telefoon (optioneel)"
                    type="tel"
                    autoComplete="tel"
                    value={phone}
                    onChange={setPhone}
                    className="mt-5"
                    testId="lead-phone"
                  />

                  <div className="mt-5">
                    <label className="overline" htmlFor="lead-message">
                      Bericht *
                    </label>
                    <textarea
                      id="lead-message"
                      data-testid="lead-message"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      required
                      rows={5}
                      placeholder={
                        subject === "Aanvraag brochure"
                          ? "Stuur mij graag de uitgebreide brochure met plattegronden en foto's."
                          : subject === "Plan bezichtiging"
                          ? "Ik zou graag een bezichtiging willen plannen. Mijn voorkeur..."
                          : "Stel hier je vraag..."
                      }
                      className="mt-2 w-full border border-brand-border bg-brand-bg px-4 py-3 text-sm leading-relaxed text-brand-ink placeholder:text-brand-muted/60 focus:border-brand-moss focus:outline-none"
                    />
                  </div>

                  {/* Honeypot */}
                  <div className="absolute -left-[9999px]" aria-hidden="true">
                    <label>
                      Bedrijf
                      <input
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                      />
                    </label>
                  </div>

                  {error && (
                    <div className="mt-6 flex items-start gap-2 border border-red-200 bg-red-50 p-4 text-sm text-red-900">
                      <AlertCircle size={16} className="mt-0.5" />
                      {error}
                    </div>
                  )}

                  <div className="mt-8 flex items-center justify-between gap-4">
                    <p className="text-xs text-brand-muted">
                      Door versturen ga je akkoord met eenmalig contact.
                    </p>
                    <button
                      type="submit"
                      data-testid="lead-submit"
                      disabled={state === "loading"}
                      className="inline-flex items-center gap-2 bg-brand-moss px-8 py-4 text-sm font-medium text-white transition-colors hover:bg-brand-mossDark disabled:opacity-60"
                    >
                      {state === "loading" ? (
                        "Versturen…"
                      ) : (
                        <>
                          <Send size={14} />
                          Verstuur
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Field({ label, type, value, onChange, required, className = "", autoComplete, testId }) {
  return (
    <div className={className}>
      <label className="overline" htmlFor={testId}>
        {label}
      </label>
      <input
        id={testId}
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        autoComplete={autoComplete}
        className="mt-2 w-full border border-brand-border bg-brand-bg px-4 py-3 text-sm text-brand-ink placeholder:text-brand-muted/60 focus:border-brand-moss focus:outline-none"
      />
    </div>
  );
}

function SuccessState({ onReset }) {
  return (
    <div data-testid="lead-success" className="py-8 text-center">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-brand-moss text-brand-moss">
        <Check size={26} strokeWidth={1.5} />
      </div>
      <h3 className="mt-6 font-serif text-3xl text-brand-ink">Bedankt, je bericht is verstuurd.</h3>
      <p className="mt-3 text-brand-muted">
        We nemen doorgaans binnen 24 uur contact met je op.
      </p>
      <button
        onClick={onReset}
        className="mt-8 text-sm font-medium text-brand-moss underline"
      >
        Nog een vraag stellen
      </button>
    </div>
  );
}
